import React, { useEffect, useRef, useState } from 'react';
import ModalCloseButton from './ModalCloseButton';

/**
 * BoardPickerModal — picks one of the user's own boards to place a
 * shared list inside (or creates a new board for it).
 *
 * Walter 2026-05-11: introduced by the sharing-refinements pass.
 * Reused by:
 *   1. AcceptInvitePage — shown after sign-in for kind=list invites,
 *      before the redirect, so the shared list lands somewhere
 *      visible instead of only the "shared with me" sidebar slot.
 *   2. ListPanel + FocusedPanel ⋯-menus — "place in board…" item
 *      lets the user re-home a shared list at any time.
 *
 * Submit produces one of:
 *   { boardId }                — chosen existing board
 *   { createWithName: string } — caller should create a new board
 *                                 then call setPlacement with its id
 *
 * The parent handles both forms; this modal stays UI-only so the
 * data-side details (which template, what default mode, etc.) stay
 * in the caller's hands.
 */
export default function BoardPickerModal({
  open,
  onClose,
  onPick,                 // ({ boardId } | { createWithName }) => Promise<void> | void
  boards = [],
  // Pre-select this board id in the dropdown. AcceptInvitePage passes
  // the user's most-recently-viewed board id (when known); list-menu
  // callers pass the current board id.
  defaultBoardId,
  // What the user is placing — drives the copy. Falls back to a
  // generic message if absent.
  listTitle,
  // Owner name for the "shared by <Tom>" line. Optional.
  ownerName,
  // Title shown at top of the modal. Defaults to "place in board…".
  // AcceptInvitePage overrides this to "where should this list live?"
  // to better fit the accept-flow framing.
  title,
}) {
  // Two modes the user can pick: 'existing' (default — dropdown over
  // their own boards) and 'new' (free-text name → caller creates).
  const [mode, setMode] = useState('existing');
  const [selectedBoardId, setSelectedBoardId] = useState(defaultBoardId || '');
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const newNameRef = useRef(null);
  const selectRef = useRef(null);

  // Reset on every reopen so we don't carry stale state between
  // different shared lists or different invokers.
  //
  // Walter 2026-05-11 feedback: when the user has 0 or 1 boards (the
  // common "fresh invitee" state — they only have the auto-seeded
  // "not named yet" board), default to "+ new board" mode. Showing
  // a dropdown with one placeholder-named board feels off as the
  // first thing they see post-accept. Two or more boards: default
  // to "existing board" since they've clearly organised a workspace.
  useEffect(() => {
    if (!open) return;
    setMode(boards.length >= 2 ? 'existing' : 'new');
    setSelectedBoardId(defaultBoardId || (boards[0]?.id || ''));
    setNewName('');
    setError('');
    setSubmitting(false);
  }, [open, defaultBoardId, boards]);

  // Focus management: when the user toggles into "new board" mode,
  // jump focus into the name field so they can type immediately.
  useEffect(() => {
    if (!open) return;
    if (mode === 'new') {
      requestAnimationFrame(() => newNameRef.current?.focus());
    } else {
      requestAnimationFrame(() => selectRef.current?.focus());
    }
  }, [open, mode]);

  // Esc closes when not submitting. The board picker is a soft
  // affordance — if it's interrupted mid-flow the user just lands
  // on the default redirect, which is the "shared with me" page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, submitting]);

  if (!open) return null;

  const canSubmit =
    !submitting &&
    ((mode === 'existing' && !!selectedBoardId) ||
     (mode === 'new' && newName.trim().length > 0));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      if (mode === 'new') {
        await onPick({ createWithName: newName.trim() });
      } else {
        await onPick({ boardId: selectedBoardId });
      }
    } catch (err) {
      console.error('[BoardPickerModal] pick failed', err);
      setError(err?.message || 'something went wrong — try again');
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={() => { if (!submitting) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 2500,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.15s ease',
        padding: '20px',
      }}
    >
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
        // Walter 2026-05-26 — Enter from inside the form should
        // submit even when focus is on the <select> (existing-board
        // mode), where the browser's default treats Enter as "open
        // dropdown" instead of "submit form". Same fix as
        // NewBoardModal. The new-name <input> path is unaffected —
        // it'd submit naturally anyway, but catching at the form
        // wrapper covers both cases with one rule.
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.shiftKey) return;
          if (!canSubmit) return;
          // Don't hijack Enter when a <select> has focus AND its
          // dropdown is actively open — let the user complete their
          // selection. There's no reliable open-detection API across
          // browsers, but if the user just pressed Enter on a select,
          // odds are they're trying to confirm an already-visible
          // selection; submit is the right action.
          e.preventDefault();
          handleSubmit(e);
        }}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          maxWidth: 440,
          width: '100%',
          padding: '22px 24px 18px',
          boxShadow: 'var(--shadow-modal)',
          fontFamily: 'var(--mono)',
          position: 'relative',
        }}
      >
        {!submitting && (
          <ModalCloseButton onClick={() => onClose?.()} />
        )}
        <div style={{
          fontSize: 'calc(13px * var(--ui-scale, 1))',
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: '4px',
          paddingRight: 40,
        }}>
          {title || 'place in board…'}
        </div>

        <div style={{
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          marginBottom: '14px',
          lineHeight: 1.5,
        }}>
          {listTitle ? (
            <>
              pick a board for{' '}
              <span style={{ color: 'var(--text)' }}>"{listTitle}"</span>
              {ownerName ? <> · shared by {ownerName}</> : null}.
              {' '}edits sync to everyone; placement is just for you.
            </>
          ) : (
            <>pick a board for this shared list. edits sync to everyone; placement is just for you.</>
          )}
        </div>

        {/* Mode toggle: existing board (default) vs new board */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 14,
        }}>
          <ModeChip
            active={mode === 'existing'}
            onClick={() => setMode('existing')}
            disabled={boards.length === 0}
            title={boards.length === 0 ? 'you have no boards yet' : undefined}
          >
            existing board
          </ModeChip>
          <ModeChip
            active={mode === 'new'}
            onClick={() => setMode('new')}
          >
            + new board
          </ModeChip>
        </div>

        {mode === 'existing' ? (
          <>
            <label style={inputLabelStyle}>board</label>
            <select
              ref={selectRef}
              value={selectedBoardId}
              onChange={e => setSelectedBoardId(e.target.value)}
              disabled={submitting || boards.length === 0}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '9px 12px',
                background: 'var(--bg)',
                border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)',
                color: 'var(--text)',
                fontFamily: 'var(--mono)',
                // 16px keeps iOS Safari from auto-zooming on focus.
                fontSize: 'calc(16px * var(--ui-scale, 1))',
                outline: 'none',
                marginBottom: '12px',
              }}
            >
              {boards.length === 0 ? (
                <option value="">(no boards yet — pick "+ new board" above)</option>
              ) : (
                boards.map(b => (
                  <option key={b.id} value={b.id}>{b.name || '(untitled)'}</option>
                ))
              )}
            </select>
          </>
        ) : (
          <>
            <label style={inputLabelStyle}>new board name</label>
            <input
              ref={newNameRef}
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. shared with anna"
              disabled={submitting}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '9px 12px',
                background: 'var(--bg)',
                border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)',
                color: 'var(--text)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(16px * var(--ui-scale, 1))',
                outline: 'none',
                marginBottom: '12px',
              }}
            />
          </>
        )}

        {error && (
          <div style={{
            marginBottom: '10px',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            color: 'var(--danger)',
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'flex-end',
          gap: 8, marginTop: 6,
        }}>
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
            style={secondaryBtnStyle}
          >
            cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              ...primaryBtnStyle,
              opacity: canSubmit ? 1 : 0.55,
              cursor: canSubmit ? 'pointer' : 'default',
            }}
          >
            {submitting ? 'placing…' : 'place here'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ModeChip({ children, active, onClick, disabled, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '7px 12px',
        background: active ? 'var(--accent-wash)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-dim)',
        border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
      }}
    >
      {children}
    </button>
  );
}

const inputLabelStyle = {
  display: 'block',
  fontSize: 'calc(10px * var(--ui-scale, 1))',
  color: 'var(--text-dim)',
  marginBottom: '5px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const primaryBtnStyle = {
  background: 'var(--accent)',
  color: 'var(--bg)',
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(12px * var(--ui-scale, 1))',
  fontWeight: 600,
  padding: '8px 16px',
};

const secondaryBtnStyle = {
  background: 'transparent',
  color: 'var(--text-dim)',
  border: '1px solid var(--border-bright)',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(12px * var(--ui-scale, 1))',
  padding: '8px 14px',
  cursor: 'pointer',
};
